import {toGetCommentsList, toGetNextCommentsList} from "../api/getCommentsList.js";
import {NewFormState} from "./utils/createDataObject.js";
import {fetchWithAuth} from "@shared/utils/auth";
import {backendUrl} from "@shared/constants/urls/appUrls";

export default {
  namespaced: true,

  state: {
    dropdownComments: NewFormState(),
    popupComments: NewFormState(),
    contentPageComments: NewFormState(),
  },

  actions: {
    //Начать процесс загрузки списка комментариев
    async startCommentsLoadingProcess({dispatch, state, commit}, {postId, context, area}) {
      try {
        await dispatch('loadComments', {postId, context, area});
      } finally {
        return;
      }
    },
    //Загрузить список комментариев
    async loadComments({commit}, {postId, context, area}) {
      try {
        commit('toToggleReady', {status: false, context: context});
        commit('toToggleLoader', {status: true, context: context});
        const data = await toGetCommentsList(postId, area);
        commit('toToggleReady', {status: true, context: context});
        commit('currentPostId', {postId, context});
        commit('setCommentsCount', {count: data.count, context: context});
        commit('setComments', {comments: data.results, context: context});
        commit('setNextPageUrl', {url: data.next, context: context});
      } catch (error) {
        console.error('Ошибка при загрузке комментария:', error);
      } finally {
        commit('toToggleLoader', {status: true, context: context});
      }
    },
    //Подгрузить следующую страницу комментариев
    async loadNextComments({commit, state}, {context}) {
      try {
        commit('toToggleNextLoader', {status: true, context: context});
        const data = await toGetNextCommentsList(state[context].nextPageUrl);
        commit('setNewComments', {comments: data.results, context: context});
        commit('setNextPageUrl', {url: data.next, context: context});
      } catch (error) {
        console.error('Ошибка при загрузке комментария:', error);
      } finally {
        commit('toToggleNextLoader', {status: false, context: context});
      }
    },
    //Добавить новый коммментарий к списку
    async addNewComment({commit, state}, {comment, context}) {
      state[context].childFormCommentId ?
        commit('setNewAnswerToComment', {comment, context}) :
        commit('setNewComment', {comment, context});
      commit('setChildFormCommentId', {id: null, context: context});
      commit('setCommentsCount', {count: state[context].commentsCount + 1, context: context});
    },
    //Закрепить коментарий
    async pinComment({commit, state}, {commentId, context, objectPk}) {
      try {
        await fetchWithAuth(`${backendUrl}/api/comments/pin/${commentId}/`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
        });
        commit('setPinnedComment', {commentId, context});
      } catch (error) {
        console.error('Ошибка при закреплении комментария:', error);
      }
    },
    //Открепить коментарий
    async unpinComment({commit, state}, {commentId, context, objectPk}) {
      try {
        await fetchWithAuth(`${backendUrl}/api/comments/pin/${commentId}/`, {
          method: 'DELETE',
          headers: {'Content-Type': 'application/json'},
        });
        commit('setUnpinComment', {commentId, context});
      } catch (error) {
        console.error('Ошибка при закреплении комментария:', error);
      }
    },
  },

  mutations: {
    //Определить статус загрузки комментариев
    toToggleLoader(state, {status, context}) {
      state[context].isLoading = status;
    },
    //Определить статус загрузки следующих комментариев
    toToggleNextLoader(state, {status, context}) {
      state[context].isLoadingNext = status;
    },
    //Определить статус готовности комментариев к выдаче
    toToggleReady(state, {status, context}) {
      state[context].isReady = status;
    },
    //Сохранить ID поста, к которому принадлежит список комментариев
    currentPostId(state, {postId, context}) {
      state[context].currentPostId = postId;
    },
    //Сохранить кол-во комментариев
    setCommentsCount(state, {count, context}) {
      state[context].commentsCount = count;
    },
    //Сохранить список комментариев
    setComments(state, {comments, context}) {
      state[context].list = comments;
      state[context].pinnedList = comments.filter(comment => comment.pinned_comment);
    },
    //Добавить новые комментарии
    setNewComments(state, {comments, context}) {
      state[context].list.push(...comments)
    },
    //Сохранить ссылку для поучения следующей страницы комментариев
    setNextPageUrl(state, {url, context}) {
      state[context].nextPageUrl = url;
    },
    //Добавить новый комментарий
    setNewComment(state, {comment, context}) {
      state[context].list.unshift(comment);
    },
    //Добавить ответ на комментарий
    setNewAnswerToComment(state, {comment, context}) {
      const list = state[context].list;
      const parentId = comment.parent_id;
      // Найти индекс родителя
      const parentIndex = list.findIndex(item => item.id === parentId);
      if (parentIndex === -1) {
        list.push(comment);
        return;
      }
      // Найти последний индекс ответа на этого родителя
      let insertIndex = parentIndex;
      for (let i = parentIndex + 1; i < list.length; i++) {
        if (list[i].parent_id === parentId) {
          insertIndex = i;
        } else if (list[i].level <= list[parentIndex].level) {
          break;
        }
      }
      list.splice(insertIndex + 1, 0, comment);
    },
    //Записать id комментария, в котором открыта форма для ответа
    setChildFormCommentId(state, {id, isEdit, context}) {
      state[context].childFormCommentId = id;
      state[context].isEditMode = typeof isEdit !== undefined ? isEdit : false;
    },
    //Указать параметры для перехода к комментариям
    setScrollTarget(state, {isComments, idComment, context, isFocusStart}) {
      state[context].isScrollToComments = isComments;
      state[context].targetCommentsId = idComment;
      state[context].isFocusStart = isFocusStart || false;
    },
    //Клонировать данные из попапа на страницу
    clonePopupCommentsToPage(state) {
      state.contentPageComments = JSON.parse(JSON.stringify(state.popupComments));
    },
    //Изменить объект комментария
    toEditCommentObject(state, {data, context}) {
      const obj = state[context].list.find(item => item.id === data.id);
      obj['comment'] = data.comment;
      obj['image_url'] = data.image_url;
      obj['is_edited'] = data.is_edited;
    },
    //Очистить список комментариев
    clearList(state, {context}) {
      state[context].list = [];
    },
    //Закрепить комментарий
    setPinnedComment(state, {commentId, context}) {
      const list = state[context].list;
      const pinnedList = state[context].pinnedList;
      const comment = list.find(item => item.id === commentId);
      if (comment && !pinnedList.some(item => item.id === commentId)) {
        comment.pinned_comment = true;
        pinnedList.push(comment);
      }
    },
    //Открепить комментарий
    setUnpinComment(state, {commentId, context}) {
      const list = state[context].list;
      const pinnedList = state[context].pinnedList;
      const comment = list.find(item => item.id === commentId);
      if (comment) {
        comment.pinned_comment = false;
        const index = pinnedList.findIndex(item => item.id === commentId);
        if (index !== -1) pinnedList.splice(index, 1);
      }
    },
  },
}

/*
  v1.0.0
  Список комментариев, а так же состояния виджета с комментариями выделено в два объекта.
  ----
  PopupComments - хранит комментарии, которые отобразятся в попапе
  ContentPageComments для контентной страницы
  ----
  Существование двух объектов необходимо, так как одновременно могут быть отображены и попап и контентая страница с
  разными постами. В текущей концепции хранения данных, если мы будем иметь только 1 экземпляр с комментариями
  и в попапе и на контентной странице,  список комментариев будет один и тот же и для попапа, и для страницы, хотя они
  отображают разные посты
  ----
  В action-ы необходимо передать объект с данными, а также вложить в объект поле context
  context будет опеределять, к какому объекту обращаться.
  Соответсвенно вызывая мутации или action мы передаем

  {
    context: 'popupComments' или 'contentPageComments'
    ...какие-то данные
  }
*/
